import { ref } from 'vue'
import { useToast } from './useToast.js'

// В dev режиме используем NestJS бэкенд на localhost:3000, в production - переменная окружения или дефолтный URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.DEV ? 'http://localhost:3000/api/' : '/api/')

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

const API_ERROR_LABELS = {
  invalid_secret: 'Интеграция временно недоступна',
  unauthorized: 'Требуется авторизация',
  forbidden: 'Недостаточно прав для этого действия',
  not_found: 'Запрошенные данные не найдены',
  request_failed: 'Не удалось связаться с сервером',
  service_unavailable: 'Сервис временно недоступен'
}

const apiErrorMessage = (payload, status) => {
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message.trim()
  }
  const code = typeof payload?.error === 'string' ? payload.error.trim() : ''
  if (API_ERROR_LABELS[code]) return API_ERROR_LABELS[code]
  if (status >= 500) return 'Сервис временно недоступен. Попробуйте ещё раз'
  if (status === 404) return 'Запрошенные данные не найдены'
  if (status === 403) return 'Недостаточно прав для этого действия'
  return `Не удалось выполнить запрос – код ${status}`
}

export function useApi() {
  const isLoading = ref(false)
  const error = ref(null)

  // Получение токена из localStorage
  const getAuthToken = () => {
    try {
      return localStorage.getItem('auth_token')
    } catch (e) {
      return null
    }
  }

  // Базовый запрос к API с автоматическими повторами
  const apiRequest = async (endpoint, options = {}, retryCount = 0) => {
    const url = API_BASE_URL + endpoint
    const token = getAuthToken()

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      }
    }

    if (token) {
      defaultOptions.headers['Authorization'] = `Bearer ${token}`
    }

    const config = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {})
      }
    }

    if (typeof FormData !== 'undefined' && config.body instanceof FormData) {
      delete config.headers['Content-Type']
    }

    try {
      isLoading.value = true
      error.value = null

      const response = await fetch(url, config)

      // Если 401, пытаемся обновить токен или перенаправляем на авторизацию
      // 401 не критична - многие функции работают без авторизации
      if (response.status === 401) {
        if (retryCount < MAX_RETRIES) {
          // Удаляем невалидный токен
          localStorage.removeItem('auth_token')
          // Пробуем еще раз без токена
          return apiRequest(endpoint, options, retryCount + 1)
        }
        // Для 401 не выбрасываем ошибку, возвращаем пустой результат
        // Авторизация не обязательна для многих функций
        try {
          const errorData = await response.json().catch(() => ({}))
          // Тихо логируем только в dev режиме
          if (import.meta.env.DEV) {
            console.debug('[API] Authorization required (not critical):', endpoint)
          }
          // Возвращаем пустой результат вместо ошибки
          return { success: false, data: null, message: errorData.message || 'Требуется авторизация' }
        } catch {
          return { success: false, data: null }
        }
      }

      // Если 429 (Too Many Requests), ждем и повторяем
      if (response.status === 429 && retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)))
        return apiRequest(endpoint, options, retryCount + 1)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(apiErrorMessage(errorData, response.status))
      }

      const data = await response.json()

      // Проверяем формат ответа API
      if (data.success === false) {
        throw new Error(data.message || data.error || 'Ошибка API')
      }

      return data

    } catch (err) {
      error.value = err.message
      
      // Показываем toast только для критических ошибок (не для авторизации - она не обязательна)
      // Ошибки авторизации не критичны, так как многие функции работают без авторизации
      const errorMsg = err.message?.toLowerCase() || ''
      const isAuthError = errorMsg.includes('авторизац') || 
                          errorMsg.includes('authorization') ||
                          (errorMsg.includes('header') && errorMsg.includes('required')) ||
                          (errorMsg.includes('authorization') && errorMsg.includes('required')) ||
                          errorMsg.includes('token') ||
                          errorMsg.includes('unauthorized')
      
      // Для ошибок авторизации не логируем и не показываем toast
      if (isAuthError) {
        // Тихо логируем только в dev режиме
        if (import.meta.env.DEV) {
          console.debug('[API] Auth error (silent):', endpoint)
        }
        // Возвращаем пустой результат вместо ошибки
        return { success: false, data: null }
      }
      
      // Для остальных ошибок показываем toast только после всех попыток
      if (retryCount === MAX_RETRIES) {
        const toastInstance = useToast()
        toastInstance.showToast(err.message || 'Произошла ошибка', 'error')
      }
      
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // GET запрос
  const get = async (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    return apiRequest(url, { method: 'GET' })
  }

  // POST запрос
  const post = async (endpoint, data = {}) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData
    return apiRequest(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data)
    })
  }

  // PUT запрос
  const put = async (endpoint, data = {}) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData
    return apiRequest(endpoint, {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data)
    })
  }

  // DELETE запрос
  const del = async (endpoint, data = {}) => {
    const options = { method: 'DELETE' }
    if (Object.keys(data).length > 0) {
      options.body = JSON.stringify(data)
    }
    return apiRequest(endpoint, options)
  }

  return {
    get,
    post,
    put,
    delete: del,
    isLoading,
    error
  }
}
